// alternative-components/compactWidgetsCardDetails/CashTrendWidgets.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DirhamSymbol } from "dirham-symbol";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardAccountsCashPositionApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { gxIsArray } from "@/app/utils/helpers";
import {
  mapCashPositionToChartPoints,
  getLatestChangePercent,
  type DashboardAccountsCashPositionRawItem,
} from "../../mappers/cashPositionMapper";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import "./styles/CompactWidgets.scss";

interface CashTrendWidgetsProps {
  title?: string;
}

const PERIOD_LABEL = "30 D";
const PERIOD_DAYS = 30;

const shortAmount = (amount: number): string => {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (abs >= 1_000_000_000)
    return `${sign}${Math.floor(abs / 1_000_000_000)} B`;
  if (abs >= 1_000_000) return `${sign}${Math.floor(abs / 1_000_000)} M`;
  if (abs >= 1_000) return `${sign}${Math.floor(abs / 1_000)} K`;
  return `${sign}${abs}`;
};

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const buildDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - PERIOD_DAYS);
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

const CashTrendYAxisTick = (props: {
  x?: number;
  y?: number;
  payload?: { value: number };
}) => {
  const { x = 0, y = 0, payload } = props;
  if (!payload) return null;

  return (
    <foreignObject x={x - 60} y={y - 9} width={56} height={18}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 4,
          fontSize: 12,
          color: "#999",
          lineHeight: "18px",
          whiteSpace: "nowrap",
        }}
      >
        <DirhamSymbol />
        <span>{shortAmount(payload.value)}</span>
      </div>
    </foreignObject>
  );
};

const CashTrendWidgets = ({ title }: CashTrendWidgetsProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";

  const [rawData, setRawData] = useState<
    DashboardAccountsCashPositionRawItem[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!currencyCode) return;
    let isMounted = true;

    const loadCashTrend = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const { startDate, endDate } = buildDateRange();

        const payload = {
          pageNo: 0,
          pageSize: 0,
          filterBy2List: [
            { filterKey: "CURRENCY_CODE", filterStringValue: currencyCode },
            { filterKey: "START_DATE", filterStringValue: startDate },
            { filterKey: "END_DATE", filterStringValue: endDate },
          ],
        };

        const response = await gxDashboardAccountsCashPositionApi(payload);
        if (!isMounted) return;

        if (response?.status === 1 && gxIsArray(response?.data)) {
          setRawData(response.data);
        } else {
          setRawData([]);
        }
      } catch (err) {
        console.log("Error in loadCashTrend::", err);
        if (isMounted) {
          setHasError(true);
          setRawData([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadCashTrend();

    return () => {
      isMounted = false;
    };
  }, [currencyCode]);

  const points = useMemo(
    () => mapCashPositionToChartPoints(rawData),
    [rawData],
  );

  //
  const maxValue = useMemo(() => {
    if (!points.length) return 0;

    return Math.max(...points.map((x) => x.value));
  }, [points]);

  const yAxisMax = useMemo(() => {
    if (maxValue <= 0) return 1000;

    return Math.ceil(maxValue / 150000) * 150000;
  }, [maxValue]);
  //

  const { percent, direction } = useMemo(
    () => getLatestChangePercent(rawData),
    [rawData],
  );

  const isPositive = direction === "up";

  return (
    <div className="gx-cash-trend-card">
      <div className="gx-cash-trend-card__header">
        <h3 className="gx-cash-trend-card__title gx-p18-b">
          {title || "Cash trend"} &middot; {PERIOD_LABEL}
        </h3>
        <span
          className={`gx-cash-trend-card__change gx-cash-trend-card__change--${direction}`}
        >
          {isPositive ? "\u25B2" : "\u25BC"} {percent}%
        </span>
      </div>

      <div className="relative">
        {isLoading && (
          <div className="gx-bene-form-container-loading-overlay">
            <SubmitLoading />
          </div>
        )}

        {!isLoading && hasError && (
          <p className="gx-p14-r gx-error-text">
            {"Failed to load cash trend"}
          </p>
        )}

        {!isLoading && !hasError && (
          <AreaChart
            style={{ width: "100%", height: 180 }}
            margin={{ left: 10, bottom: -5, top: 10, right: 10 }}
            data={points}
          >
            <defs>
              <linearGradient id="gxCashTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2C3E9E" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#2C3E9E" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} horizontal strokeDasharray="4 4" />

            <XAxis
              axisLine={{ stroke: "#d9d9d9" }}
              tickLine={false}
              dataKey="name"
              tick={{ fill: "#999", fontSize: 12 }}
            />

            {/* <YAxis
              axisLine={false}
              tickLine={false}
              tick={<CashTrendYAxisTick />}
              width={70}
            /> */}

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={<CashTrendYAxisTick />}
              width={70}
              domain={[0, yAxisMax]}
              tickCount={5}
            />

            <Tooltip
              formatter={(value) => {
                const formatted = gxCurrencyFormat(
                  Number(value),
                  currencyCode || "AED",
                  undefined,
                  { showSymbol: true },
                );
                return [
                  <span
                    key="amt"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {formatted.symbol} {formatted.integer}
                    {formatted.decimal}
                  </span>,
                  "",
                ];
              }}
              labelFormatter={(label) => label}
              contentStyle={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              }}
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#2C3E9E"
              strokeWidth={2}
              fill="url(#gxCashTrendFill)"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        )}
      </div>
    </div>
  );
};

export default CashTrendWidgets;
